import { useState } from 'react'
import { MdCheckCircle, MdClose, MdInfoOutline } from 'react-icons/md'

interface ImportNotesBannerProps {
  problemsToFix: string[]
  wingmanNotes: string[]
  isCheckingPostcodes: boolean
}

const AMBER_CLASS_NAMES = {
  box: 'border-amber-200 bg-amber-50',
  icon: 'text-amber-600',
  title: 'text-amber-900',
  dismissButton: 'text-amber-500 hover:bg-amber-100 hover:text-amber-700',
}

const GREEN_CLASS_NAMES: typeof AMBER_CLASS_NAMES = {
  box: 'border-green-200 bg-green-50',
  icon: 'text-green-600',
  title: 'text-green-900',
  dismissButton: 'text-green-500 hover:bg-green-100 hover:text-green-700',
}

function describeTitle(isCheckingPostcodes: boolean, problemsLeft: number): string {
  if (isCheckingPostcodes) return 'Checking the imported rate card…'
  if (problemsLeft === 0) return 'Nothing left to fix from the import.'
  return 'Still to fix on the imported rate card'
}

export function ImportNotesBanner({ problemsToFix, wingmanNotes, isCheckingPostcodes }: ImportNotesBannerProps) {
  const [isDismissed, setIsDismissed] = useState(false)

  if (isDismissed) return null

  const isEverythingFixed = !isCheckingPostcodes && problemsToFix.length === 0
  const classNames = isEverythingFixed ? GREEN_CLASS_NAMES : AMBER_CLASS_NAMES
  const StatusIcon = isEverythingFixed ? MdCheckCircle : MdInfoOutline

  return (
    <div className={`mx-4 mt-3 lg:mx-8 rounded-lg border px-4 py-3 ${classNames.box}`}>
      <div className='flex items-start gap-3'>
        <StatusIcon className={`mt-0.5 h-5 w-5 shrink-0 ${classNames.icon}`} aria-hidden='true' />
        <div className='flex-1 min-w-0'>
          <p className={`text-sm font-medium ${classNames.title}`}>
            {describeTitle(isCheckingPostcodes, problemsToFix.length)}
            {problemsToFix.length > 0 && (
              <span className='ml-2 text-xs font-normal text-amber-700'>{`${problemsToFix.length} left`}</span>
            )}
          </p>
          {problemsToFix.length > 0 && (
            <ul className='mt-1.5 space-y-1 list-disc pl-5 text-xs text-amber-800'>
              {problemsToFix.map((problem) => (
                <li key={problem}>{problem}</li>
              ))}
            </ul>
          )}
          {wingmanNotes.length > 0 && (
            <details open className='mt-2'>
              <summary className='cursor-pointer text-xs font-medium text-gray-600'>
                {`Wingman's notes on the file (${wingmanNotes.length})`}
              </summary>
              <ul className='mt-1 max-h-[30vh] space-y-1 overflow-y-auto list-disc pl-5 pr-1 text-xs text-gray-600'>
                {wingmanNotes.map((note) => (
                  <li key={note}>{note}</li>
                ))}
              </ul>
            </details>
          )}
        </div>
        <button
          type='button'
          onClick={() => setIsDismissed(true)}
          className={`shrink-0 rounded p-1 ${classNames.dismissButton}`}
          aria-label='Dismiss import notes'
        >
          <MdClose className='h-4 w-4' />
        </button>
      </div>
    </div>
  )
}
